import React, { ReactNode } from 'react';
import { colors, typography, spacing } from '../../styles/design-system';

export interface SelectOption {
  value: string | number;
  label: string;
  disabled?: boolean;
}

interface BaseFieldProps {
  label?: string;
  required?: boolean;
  error?: string;
  hint?: string;
  disabled?: boolean;
  style?: React.CSSProperties;
  labelStyle?: React.CSSProperties;
  vertical?: boolean;
}

interface InputFieldProps extends BaseFieldProps {
  type: 'text' | 'password' | 'email' | 'number' | 'time' | 'date';
  value: string | number;
  onChange: (value: string) => void;
  placeholder?: string;
  min?: number;
  max?: number;
}

interface SelectFieldProps extends BaseFieldProps {
  type: 'select';
  value: string | number;
  onChange: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
}

interface TextareaFieldProps extends BaseFieldProps {
  type: 'textarea';
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  rows?: number;
}

interface CheckboxFieldProps extends BaseFieldProps {
  type: 'checkbox';
  checked: boolean;
  onChange: (checked: boolean) => void;
  checkboxLabel?: string;
}

interface CustomFieldProps extends BaseFieldProps {
  type: 'custom';
  children: ReactNode;
}

type FormFieldProps =
  | InputFieldProps
  | SelectFieldProps
  | TextareaFieldProps
  | CheckboxFieldProps
  | CustomFieldProps;

const FormField: React.FC<FormFieldProps> = (props) => {
  const {
    label,
    required,
    error,
    hint,
    disabled,
    style,
    labelStyle,
    vertical = true,
  } = props;

  const containerStyle: React.CSSProperties = {
    ...(vertical ? styles.containerVertical : styles.containerHorizontal),
    ...style,
  };

  const renderInput = () => {
    switch (props.type) {
      case 'text':
      case 'password':
      case 'email':
      case 'number':
      case 'time':
      case 'date':
        return (
          <input
            type={props.type}
            value={props.value}
            onChange={(e) => props.onChange(e.target.value)}
            placeholder={props.placeholder}
            disabled={disabled}
            min={props.min}
            max={props.max}
            style={{
              ...styles.input,
              ...(error ? styles.inputError : {}),
            }}
          />
        );

      case 'select':
        return (
          <select
            value={props.value}
            onChange={(e) => props.onChange(e.target.value)}
            disabled={disabled}
            style={{
              ...styles.select,
              ...(error ? styles.inputError : {}),
            }}
          >
            {props.placeholder && (
              <option value="">{props.placeholder}</option>
            )}
            {props.options.map((opt) => (
              <option key={opt.value} value={opt.value} disabled={opt.disabled}>
                {opt.label}
              </option>
            ))}
          </select>
        );

      case 'textarea':
        return (
          <textarea
            value={props.value}
            onChange={(e) => props.onChange(e.target.value)}
            placeholder={props.placeholder}
            disabled={disabled}
            rows={props.rows || 4}
            style={{
              ...styles.textarea,
              ...(error ? styles.inputError : {}),
            }}
          />
        );

      case 'checkbox':
        return (
          <label style={styles.checkboxContainer}>
            <input
              type="checkbox"
              checked={props.checked}
              onChange={(e) => props.onChange(e.target.checked)}
              disabled={disabled}
              style={styles.checkbox}
            />
            {props.checkboxLabel && (
              <span style={styles.checkboxLabel}>{props.checkboxLabel}</span>
            )}
          </label>
        );

      case 'custom':
        return props.children;

      default:
        return null;
    }
  };

  return (
    <div style={containerStyle}>
      {label && (
        <label style={{ ...styles.label, ...labelStyle }}>
          {label}
          {required && <span style={styles.required}>*</span>}
        </label>
      )}
      {renderInput()}
      {error && <span style={styles.error}>{error}</span>}
      {hint && !error && <span style={styles.hint}>{hint}</span>}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  containerVertical: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing[2],
  },
  containerHorizontal: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing[3],
  },
  label: {
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.medium,
    color: colors.gray[800],
    whiteSpace: 'nowrap',
  },
  required: {
    color: colors.status.error,
    marginLeft: spacing[1],
  },
  input: {
    padding: `${spacing[2]} ${spacing[4]}`,
    fontSize: typography.fontSize.base,
    border: `1px solid ${colors.gray[300]}`,
    borderRadius: '0',
    backgroundColor: colors.white,
    outline: 'none',
    transition: 'border-color 0.15s',
  },
  select: {
    padding: `${spacing[2]} ${spacing[4]}`,
    fontSize: typography.fontSize.base,
    border: `1px solid ${colors.gray[300]}`,
    borderRadius: '0',
    backgroundColor: colors.white,
    minWidth: '120px',
    outline: 'none',
    cursor: 'pointer',
  },
  textarea: {
    width: '100%',
    padding: spacing[4],
    fontSize: typography.fontSize.base,
    border: `1px solid ${colors.gray[300]}`,
    borderRadius: '0',
    resize: 'vertical',
    outline: 'none',
  },
  inputError: {
    borderColor: colors.status.error,
  },
  checkboxContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing[2],
    cursor: 'pointer',
  },
  checkbox: {
    cursor: 'pointer',
  },
  checkboxLabel: {
    fontSize: typography.fontSize.base,
    color: colors.gray[800],
  },
  error: {
    fontSize: typography.fontSize.sm,
    color: colors.status.error,
  },
  hint: {
    fontSize: typography.fontSize.sm,
    color: colors.gray[500],
  },
};

export default FormField;
